/**
 * <product-recommendations>
 *
 * Charge les recommandations produit à l'approche du viewport.
 *
 * Priorité aux produits complémentaires configurés dans Search & Discovery
 * (intent=complementary), complétés si besoin par les recommandations
 * algorithmiques de Shopify (intent=related, valeur par défaut de l'API).
 *
 * Les deux appels partent en parallèle. `fetchSlides` encapsule ses erreurs,
 * donc un appel en échec n'empêche pas l'autre de s'afficher. La limite est
 * lue depuis data-url : le Liquid reste la seule source de vérité.
 *
 * Ce fichier est copié tel quel vers le thème (pas de transpilation sur cette
 * couche) : pas d'optional chaining, pas de replaceChildren.
 */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.observer || !this.dataset.url) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;

        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 200px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  get limit() {
    const limit = parseInt(this.buildUrl().searchParams.get('limit'), 10);

    // L'API Shopify plafonne limit à 10 et retombe sur 10 par défaut :
    // inutile de viser plus haut, on ne remplirait jamais.
    if (!Number.isInteger(limit) || limit < 1) return 10;

    return Math.min(limit, 10);
  }

  buildUrl(intent) {
    const url = new URL(this.dataset.url, window.location.origin);

    if (intent) url.searchParams.set('intent', intent);

    return url;
  }

  /**
   * Renvoie toujours un objet exploitable : un appel en échec ne doit pas
   * empêcher l'autre de s'afficher.
   */
  async fetchSlides(intent) {
    try {
      const response = await fetch(this.buildUrl(intent).toString());

      if (!response.ok) {
        throw new Error(response.status + ' ' + response.statusText);
      }

      const holder = document.createElement('div');
      holder.innerHTML = await response.text();

      const section = holder.querySelector('product-recommendations');

      return {
        section: section,
        slides: section
          ? Array.from(section.querySelectorAll('.splide__slide'))
          : [],
      };
    } catch (error) {
      console.error(
        '[product-recommendations] ' + (intent || 'related') + ' :',
        error
      );

      return { section: null, slides: [] };
    }
  }

  async load() {
    const limit = this.limit;

    // `fetchSlides` ne rejette jamais : Promise.all se comporte ici comme un
    // allSettled, un appel en échec n'empêche pas l'autre de s'afficher.
    const [complementary, related] = await Promise.all([
      this.fetchSlides('complementary'),
      this.fetchSlides(),
    ]);

    // Assez de complémentaires : les automatiques sont ignorées.
    if (complementary.slides.length >= limit) {
      this.render(complementary.section, complementary.slides.slice(0, limit));
      return;
    }

    // Aucun complémentaire configuré : recommandations automatiques seules.
    if (complementary.slides.length === 0) {
      this.render(related.section, related.slides.slice(0, limit));
      return;
    }

    // Complétion sans doublon, dans l'ordre renvoyé par Shopify.
    const slides = complementary.slides.slice();
    const seen = new Set(slides.map((slide) => slide.dataset.productId));

    for (const slide of related.slides) {
      if (slides.length >= limit) break;
      if (seen.has(slide.dataset.productId)) continue;

      seen.add(slide.dataset.productId);
      slides.push(slide);
    }

    this.render(complementary.section, slides);
  }

  render(section, slides) {
    if (!section || slides.length === 0) return;

    const list = section.querySelector('.splide__list');

    if (list) {
      list.innerHTML = '';
      slides.forEach((slide) => list.appendChild(slide));
    }

    this.innerHTML = section.innerHTML;
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
